import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { actions } from '../store/authentication/AuthSlice';
import { login } from '../api/services/AuthService';
import { LocalRoutes } from '../enum/LocalRoutes';
import { Button, Form } from 'react-bootstrap';

const initialValues = {
    email: '',
    password: '',
};

const Login = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const LoginSchema = Yup.object().shape({
        email: Yup.string()
            .email('Wrong email format')
            .min(3, 'Minimum 3 symbols')
            .max(50, 'Maximum 50 symbols')
            .required('Required field'),
        password: Yup.string().min(3, 'Minimum 3 symbols').max(50, 'Maximum 50 symbols').required('Required field'),
    });

    const formik = useFormik({
        initialValues,
        validationSchema: LoginSchema,
        onSubmit: async (values, { setStatus, setSubmitting }) => {
            setLoading(true);
            try {
                const data = await login(values.email, values.password);

                localStorage.setItem('token', data.token);
                localStorage.setItem('adminAcl', JSON.stringify(data?.userData?.adminAcl ?? []));
                localStorage.setItem('refreshToken', data?.refreshToken);
                localStorage.setItem('appVersion', data?.userData.appVersion);

                dispatch(actions.setUser(data?.userData));
                navigate(LocalRoutes.VERIFICATION);
            } catch {
                setSubmitting(false);
                setStatus('The login detail is incorrect');
            } finally {
                setLoading(false);
            }
        },
    });

    const getInputClasses = (fieldName: keyof typeof formik.values) => {
        if (formik.touched[fieldName] && formik.errors[fieldName]) {
            return 'is-invalid';
        }

        if (formik.touched[fieldName] && !formik.errors[fieldName]) {
            return 'is-valid';
        }

        return '';
    };

    return (
        <div className="login-form login-signin" id="kt_login_signin_form">
            <div className="text-center mb-10 mb-lg-20">
                <h3 className="font-size-h1">Login Account</h3>
                <p className="text-muted font-weight-bold">Enter your username and password</p>
            </div>

            <Form onSubmit={formik.handleSubmit} className="fv-plugins-bootstrap fv-plugins-framework">
                {!!formik.status && (
                    <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
                        <div className="alert-text font-weight-bold">{formik.status}</div>
                    </div>
                )}

                <Form.Group className="fv-plugins-icon-container mb-5">
                    <input
                        placeholder="Email"
                        type="email"
                        className={`form-control form-control-solid py-4 px-6 ${getInputClasses('email')}`}
                        {...formik.getFieldProps('email')}
                    />
                    {formik.touched.email && formik.errors.email && (
                        <div className="invalid-feedback">{formik.errors.email}</div>
                    )}
                </Form.Group>

                <Form.Group className="fv-plugins-icon-container mb-5">
                    <input
                        placeholder="Password"
                        type="password"
                        className={`form-control form-control-solid py-4 px-6 ${getInputClasses('password')}`}
                        {...formik.getFieldProps('password')}
                    />
                    {formik.touched.password && formik.errors.password && (
                        <div className="invalid-feedback">{formik.errors.password}</div>
                    )}
                </Form.Group>

                <Form.Group className="form-group d-flex flex-wrap justify-content-between align-items-center">
                    <Button type="submit" disabled={formik.isSubmitting} className="px-9 py-3 my-3">
                        <span>Sign In</span>
                        {loading && <span className="ml-3 spinner spinner-white"></span>}
                    </Button>
                </Form.Group>
            </Form>
        </div>
    );
};

export default Login;
